import firebaseAdsService from '@/services/firebaseAdsService';
// Ad content now comes from our API, with Firestore kept as a one-release
// fallback. See services/adsSource.js for the cutover contract.
import adsSource from '@/services/adsSource';

// Cache configuration
const CACHE_CONFIG = {
  VUEX_TTL: 3 * 24 * 60 * 60 * 1000,      // 3 days in memory
  LOCALSTORAGE_TTL: 3 * 24 * 60 * 60 * 1000, // 3 days persistent
  LOCALSTORAGE_KEY: 'dentcloud_ads_cache',
  CACHE_VERSION: '1.2' // Updated version for Google Sheets integration
};

class AdsCache {
  constructor() {
    this.vuexCache = {
      data: null,
      timestamp: null,
      ttl: CACHE_CONFIG.VUEX_TTL
    };
    
    this.isLoading = { value: false };
    this.lastError = { value: null };
    this.lastFetchTime = { value: null };
    this.pendingFetch = null; // Track ongoing fetch requests
  }

  /**
   * Get ads data with database version checking and multi-level caching
   * @param {string} databaseVersion - Current ads_version from database (optional)
   * @returns {Promise<Array>} Ads data
   */
  async getAdsWithVersion(databaseVersion = null) {
    // Invariant: no code path may hand ad content to an AD_FREE branch — not a
    // fetch, not a cache read, not a store hydration. Every branch below this
    // point (Vuex cache, localStorage cache, the old-format migration, and the
    // stale-data error fallback) can return real ad content WITHOUT ever
    // calling adsSource — that's the whole point of a cache — so the guard
    // has to sit here, above all of them, not inside adsSource.js. Clearing
    // the cache rather than just skipping it means content fetched before the
    // branch became AD_FREE (or cached under this shared, non-branch-scoped
    // localStorage key from a different branch entirely) can never resurface
    // through this method either.
    if (adsSource.shouldSkipAds()) {
      this.clearCache();
      return [];
    }

    try {
      // Skip cache for dev/staging - fetch fresh data every time
      const isDevelopmentOrStaging = process.env.VUE_APP_MODE === 'dev' || 
                                    process.env.VUE_APP_MODE === 'staging';
      
      if (isDevelopmentOrStaging) {
        this.isLoading.value = true;
        this.lastError.value = null;
        
        const freshData = await adsSource.getFormattedAds();
        this.isLoading.value = false;
        return freshData;
      }

      // Production logic - with caching
      // If there's already a pending fetch, wait for it
      if (this.pendingFetch) {
        return await this.pendingFetch;
      }

      this.isLoading.value = true;
      this.lastError.value = null;

      // If no database version provided, fetch it from API
      let currentDatabaseVersion = databaseVersion;
      if (!currentDatabaseVersion) {
        try {
          currentDatabaseVersion = await this.fetchDatabaseVersion();
        } catch (error) {
          currentDatabaseVersion = '1'; // Default fallback
        }
      }

      // Check if cached version matches database version
      const cachedVersion = this.getCachedVersion();
      if (cachedVersion && cachedVersion !== currentDatabaseVersion) {
        this.clearCache();
        this.pendingFetch = this.fetchFreshDataWithVersion(currentDatabaseVersion);
        return await this.pendingFetch;
      }

      // Check Vuex cache first (fastest)
      const vuexData = this.getFromVuexCache();
      if (vuexData) {
        return vuexData;
      }

      // Check localStorage cache (persistent) - but migrate old format if needed
      const localData = this.getFromLocalStorageWithVersion(currentDatabaseVersion);
      if (localData) {
        this.saveToVuexCache(localData); // Populate Vuex cache
        return localData;
      }

      // Check for old format cache and migrate
      const oldFormatData = this.migrateOldCacheFormat(currentDatabaseVersion);
      if (oldFormatData) {
        this.saveToVuexCache(oldFormatData);
        return oldFormatData;
      }

      // Fetch fresh data from Firebase
      this.pendingFetch = this.fetchFreshDataWithVersion(currentDatabaseVersion);
      return await this.pendingFetch;

    } catch (error) {
      this.lastError.value = error;
      
      // Try to return stale data if available
      const staleData = this.getStaleData();
      if (staleData) {
        return staleData;
      }
      
      return []; // Return empty array as fallback
    } finally {
      this.isLoading.value = false;
      this.lastFetchTime.value = new Date();
      this.pendingFetch = null; // Clear pending fetch
    }
  }

  /**
   * Get ads data with multi-level caching and concurrent request deduplication
   * @param {boolean} forceRefresh - Skip cache and fetch fresh data
   * @returns {Promise<Array>} Ads data
   */
  async getAds(forceRefresh = false) {
    // Same invariant as getAdsWithVersion above: this method's cache branches
    // can also serve content without calling adsSource, so it needs its own
    // guard rather than relying on the one in adsSource.js.
    if (adsSource.shouldSkipAds()) {
      this.clearCache();
      return [];
    }

    try {
      // If there's already a pending fetch, wait for it instead of creating a new one
      if (this.pendingFetch && !forceRefresh) {
        return await this.pendingFetch;
      }

      this.isLoading.value = true;
      this.lastError.value = null;

      // Skip cache if force refresh requested
      if (forceRefresh) {
        this.pendingFetch = this.fetchFreshData();
        return await this.pendingFetch;
      }

      // Check Vuex cache first (fastest)
      const vuexData = this.getFromVuexCache();
      if (vuexData) {
        return vuexData;
      }

      // Check localStorage cache (persistent)
      const localData = this.getFromLocalStorage();
      if (localData) {
        this.saveToVuexCache(localData); // Populate Vuex cache
        return localData;
      }

      // Fetch fresh data from Firebase
      this.pendingFetch = this.fetchFreshData();
      return await this.pendingFetch;

    } catch (error) {
      this.lastError.value = error;
      
      // Try to return stale data if available
      const staleData = this.getStaleData();
      if (staleData) {
        return staleData;
      }
      
      return []; // Return empty array as fallback
    } finally {
      this.isLoading.value = false;
      this.lastFetchTime.value = new Date();
      this.pendingFetch = null; // Clear pending fetch
    }
  }

  /**
   * Fetch fresh data from Firebase with version and update all caches
   * @param {string} databaseVersion - Current ads_version from database
   * @returns {Promise<Array>} Fresh ads data
   */
  async fetchFreshDataWithVersion(databaseVersion) {
    const freshData = await adsSource.getFormattedAds();
    
    // Update all cache levels with version
    this.saveToVuexCache(freshData);
    this.saveToLocalStorageWithVersion(freshData, databaseVersion);
    
    return freshData;
  }

  /**
   * Fetch fresh data from Firebase and update all caches
   * @returns {Promise<Array>} Fresh ads data
   */
  async fetchFreshData() {
    const freshData = await adsSource.getFormattedAds();
    
    // Update all cache levels
    this.saveToVuexCache(freshData);
    this.saveToLocalStorage(freshData);
    
    return freshData;
  }

  /**
   * Get data from Vuex cache if still valid
   * @returns {Array|null} Cached data or null if expired/missing
   */
  getFromVuexCache() {
    if (!this.vuexCache.data || !this.vuexCache.timestamp) {
      return null;
    }

    const age = Date.now() - this.vuexCache.timestamp;
    if (age > this.vuexCache.ttl) {
      this.vuexCache.data = null;
      this.vuexCache.timestamp = null;
      return null;
    }

    return this.vuexCache.data;
  }

  /**
   * Save data to Vuex cache
   * @param {Array} data - Ads data to cache
   */
  saveToVuexCache(data) {
    this.vuexCache.data = data;
    this.vuexCache.timestamp = Date.now();
  }

  /**
   * Get data from localStorage if still valid
   * @returns {Array|null} Cached data or null if expired/missing
   */
  getFromLocalStorage() {
    try {
      const cached = localStorage.getItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      
      // Check version compatibility
      if (parsed.version !== CACHE_CONFIG.CACHE_VERSION) {
        this.clearLocalStorage();
        return null;
      }

      // Check if expired
      const age = Date.now() - parsed.timestamp;
      if (age > CACHE_CONFIG.LOCALSTORAGE_TTL) {
        this.clearLocalStorage();
        return null;
      }

      return parsed.data;
    } catch (error) {
      this.clearLocalStorage();
      return null;
    }
  }

  /**
   * Save data to localStorage
   * @param {Array} data - Ads data to cache
   */
  saveToLocalStorage(data) {
    try {
      const cacheEntry = {
        data: data,
        timestamp: Date.now(),
        version: CACHE_CONFIG.CACHE_VERSION,
        ttl: CACHE_CONFIG.LOCALSTORAGE_TTL
      };

      localStorage.setItem(
        CACHE_CONFIG.LOCALSTORAGE_KEY, 
        JSON.stringify(cacheEntry)
      );
    } catch (error) {
      // Continue without localStorage if storage is full/unavailable
    }
  }

  /**
   * Save data to localStorage with database version
   * @param {Array} data - Ads data to cache
   * @param {string} databaseVersion - Database ads_version to store
   */
  saveToLocalStorageWithVersion(data, databaseVersion) {
    try {
      const cacheEntry = {
        data: data,
        timestamp: Date.now(),
        version: CACHE_CONFIG.CACHE_VERSION,
        databaseVersion: databaseVersion,
        ttl: CACHE_CONFIG.LOCALSTORAGE_TTL
      };

      localStorage.setItem(
        CACHE_CONFIG.LOCALSTORAGE_KEY, 
        JSON.stringify(cacheEntry)
      );
    } catch (error) {
      // Continue without localStorage if storage is full/unavailable
    }
  }

  /**
   * Get data from localStorage with version checking
   * @param {string} databaseVersion - Current database ads_version
   * @returns {Array|null} Cached data or null if expired/missing/version mismatch
   */
  getFromLocalStorageWithVersion(databaseVersion) {
    try {
      const cached = localStorage.getItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      
      // Check version compatibility
      if (parsed.version !== CACHE_CONFIG.CACHE_VERSION) {
        this.clearLocalStorage();
        return null;
      }

      // Check if database version matches cached version
      if (parsed.databaseVersion && parsed.databaseVersion !== databaseVersion) {
        this.clearLocalStorage();
        return null;
      }

      // Check if expired
      const age = Date.now() - parsed.timestamp;
      if (age > CACHE_CONFIG.LOCALSTORAGE_TTL) {
        this.clearLocalStorage();
        return null;
      }

      return parsed.data;
    } catch (error) {
      this.clearLocalStorage();
      return null;
    }
  }

  /**
   * Get cached database version from localStorage
   * @returns {string|null} Cached database version or null if not found
   */
  getCachedVersion() {
    try {
      const cached = localStorage.getItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      return parsed.databaseVersion || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetch current ads_version from database API
   * @returns {Promise<string>} Database ads_version
   */
  async fetchDatabaseVersion() {
    // Derived from the newest ad write when the API answers, so editing an ad
    // in the console busts this cache on its own. Falls back to the legacy
    // config_value counter that the old "Force Update Ads" button bumps.
    return adsSource.getAdsVersion();
  }

  /**
   * Migrate old cache format (without databaseVersion) to new format
   * @param {string} currentDatabaseVersion - Current database version to use
   * @returns {Array|null} Migrated ads data or null if no old cache
   */
  migrateOldCacheFormat(currentDatabaseVersion) {
    try {
      const cached = localStorage.getItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
      if (!cached) return null;

      const parsed = JSON.parse(cached);
      
      // Check if this is old format (has data but no databaseVersion)
      if (parsed.data && !parsed.databaseVersion && parsed.version === CACHE_CONFIG.CACHE_VERSION) {
        
        // Check if cache is still valid (not expired)
        const age = Date.now() - parsed.timestamp;
        if (age <= CACHE_CONFIG.LOCALSTORAGE_TTL) {
          // Migrate by saving with new format
          this.saveToLocalStorageWithVersion(parsed.data, currentDatabaseVersion);
          return parsed.data;
        } else {
          // Old cache expired, clear it
          this.clearLocalStorage();
        }
      }
      
      return null;
    } catch (error) {
      this.clearLocalStorage(); // Clear corrupted cache
      return null;
    }
  }

  /**
   * Get stale data for error recovery
   * @returns {Array|null} Stale data if available
   */
  getStaleData() {
    // Try Vuex cache even if expired
    if (this.vuexCache.data) {
      return this.vuexCache.data;
    }

    // Try localStorage even if expired
    try {
      const cached = localStorage.getItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.data || null;
      }
    } catch (error) {
      // Error reading stale data
    }

    return null;
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.vuexCache.data = null;
    this.vuexCache.timestamp = null;
    this.pendingFetch = null; // Clear any pending requests
    this.clearLocalStorage();
  }

  /**
   * Clear localStorage cache
   */
  clearLocalStorage() {
    try {
      localStorage.removeItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
    } catch (error) {
      // Error clearing localStorage cache
    }
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache status information
   */
  getCacheStats() {
    const vuexAge = this.vuexCache.timestamp ? Date.now() - this.vuexCache.timestamp : null;
    
    let localStorageAge = null;
    let localStorageSize = 0;
    
    try {
      const cached = localStorage.getItem(CACHE_CONFIG.LOCALSTORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        localStorageAge = Date.now() - parsed.timestamp;
        localStorageSize = cached.length;
      }
    } catch (error) {
      // Ignore localStorage errors for stats
    }

    return {
      vuex: {
        hasData: !!this.vuexCache.data,
        age: vuexAge,
        isExpired: vuexAge ? vuexAge > this.vuexCache.ttl : true
      },
      localStorage: {
        hasData: localStorageAge !== null,
        age: localStorageAge,
        size: localStorageSize,
        isExpired: localStorageAge ? localStorageAge > CACHE_CONFIG.LOCALSTORAGE_TTL : true
      },
      firebase: firebaseAdsService.getConnectionStatus(),
      isLoading: this.isLoading.value,
      lastError: this.lastError.value,
      lastFetchTime: this.lastFetchTime.value
    };
  }

  /**
   * Preload ads data (non-blocking)
   */
  async preloadAds() {
    try {
      await this.getAdsWithVersion();
    } catch (error) {
      // Don't throw - preloading is optional
    }
  }
}

// Create singleton instance
const adsCache = new AdsCache();

// Composable function for Vue components (Vue 2 compatible)
export function useAdsCache() {
  return {
    // Methods
    getAds: adsCache.getAds.bind(adsCache),
    getAdsWithVersion: adsCache.getAdsWithVersion.bind(adsCache),
    fetchFreshData: adsCache.fetchFreshData.bind(adsCache),
    clearCache: adsCache.clearCache.bind(adsCache),
    preloadAds: adsCache.preloadAds.bind(adsCache),
    getCacheStats: adsCache.getCacheStats.bind(adsCache),
    
    // State getters (Vue 2 compatible)
    get isLoading() { return adsCache.isLoading.value; },
    get lastError() { return adsCache.lastError.value; },
    get lastFetchTime() { return adsCache.lastFetchTime.value; }
  };
}

// Export cache instance for Vuex store
export { adsCache };
export default useAdsCache;